import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Address, AddressSuggestion } from '../../types/address';
import { useAddressAutocomplete, useGeocode } from '../../hooks/useMaps';
import { addressesMatch, geocodedStreetMatches } from '../../utils/address';

// US states + DC + territories. Stored as 2-letter codes to match Google's
// administrative_area_level_1 short value (e.g. "CO"). Non-US users with a
// stateProvince outside this list are handled below by injecting their
// current value as an extra option, so existing data isn't lost.
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI',
  'WY', 'PR', 'VI', 'GU', 'AS', 'MP',
];

/** The fields a street address geocodes from. Unit (street2) doesn't move the pin. */
function streetFields(addr: Address | null | undefined): Address {
  return {
    street1: addr?.street1,
    city: addr?.city,
    stateProvince: addr?.stateProvince,
    postalCode: addr?.postalCode,
    country: addr?.country,
  };
}

function sameStreetFields(a: Address, b: Address): boolean {
  const norm = (s?: string) => s?.trim().toLowerCase() ?? '';
  return (
    norm(a.street1) === norm(b.street1) &&
    norm(a.city) === norm(b.city) &&
    norm(a.stateProvince) === norm(b.stateProvince) &&
    norm(a.postalCode) === norm(b.postalCode) &&
    norm(a.country) === norm(b.country)
  );
}

interface Props {
  value: Address | null;
  onSave: (address: Address) => void;
  onCancel: () => void;
}

export function AddressEditCard({ value, onSave, onCancel }: Props) {
  const { t } = useTranslation('location');
  const [street1, setStreet1] = useState(value?.street1 ?? '');
  const [street2, setStreet2] = useState(value?.street2 ?? '');
  const [city, setCity] = useState(value?.city ?? '');
  const [stateProvince, setStateProvince] = useState(value?.stateProvince ?? '');
  const [postalCode, setPostalCode] = useState(value?.postalCode ?? '');
  const [country, setCountry] = useState(value?.country ?? '');
  const [noUnit, setNoUnit] = useState<boolean>(!!value?.noUnit);
  // Pin-only confirmation — preserved from initial value so editing a
  // pin-confirmed address doesn't silently invalidate it. Cleared when the
  // user picks a new place from autocomplete (a real street replaces the pin).
  const [pinConfirmed, setPinConfirmed] = useState<boolean>(!!value?.pinConfirmed);

  // Holds the non-visible geocoded bits (lat/lon/placeId/county/long names)
  // from the most recent suggestion pick — or the initial `value`. Visible
  // fields override these on save, but lat/lon/placeId etc. stay attached.
  const extrasRef = useRef<Partial<Address>>({
    lat: value?.lat,
    lon: value?.lon,
    placeId: value?.placeId,
    geocoded: value?.geocoded,
    county: value?.county,
    countyLong: value?.countyLong,
    stateProvinceLong: value?.stateProvinceLong,
    countryLong: value?.countryLong,
    address: value?.address,
  });

  // The visible street fields that `extrasRef`'s lat/lon were geocoded for.
  // Typing over them by hand (instead of picking a suggestion) leaves the old
  // coords behind, so handleSave compares against this and re-geocodes when
  // they differ. Otherwise the text moves and the pin doesn't.
  const geocodedForRef = useRef<Address>(streetFields(value));

  // Set once the user taps "Clear" — tells handleSave NOT to fold the original
  // `value` back in, so a from-scratch entry doesn't inherit the old place's
  // lat/lon/placeId. Reset whenever `value`'s identity changes (re-seed effect).
  const clearedRef = useRef(false);

  const [saveError, setSaveError] = useState<string | null>(null);

  // Closing the card while handleSave's geocode is in flight unmounts it; the
  // save must not land after the user cancelled it.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [sessionToken, setSessionToken] = useState<string | undefined>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streetWrapperRef = useRef<HTMLDivElement>(null);

  const autocompleteMutation = useAddressAutocomplete();
  const geocodeMutation = useGeocode();

  // Re-seed fields when the underlying address identity changes (e.g. user
  // clicks "Use my current location" while the card is open). Keyed on identity
  // fields rather than every field so in-progress typing inside the card isn't
  // clobbered.
  useEffect(() => {
    setStreet1(value?.street1 ?? '');
    setStreet2(value?.street2 ?? '');
    setCity(value?.city ?? '');
    setStateProvince(value?.stateProvince ?? '');
    setPostalCode(value?.postalCode ?? '');
    setCountry(value?.country ?? '');
    setNoUnit(!!value?.noUnit);
    setPinConfirmed(!!value?.pinConfirmed);
    extrasRef.current = {
      lat: value?.lat,
      lon: value?.lon,
      placeId: value?.placeId,
      geocoded: value?.geocoded,
      county: value?.county,
      countyLong: value?.countyLong,
      stateProvinceLong: value?.stateProvinceLong,
      countryLong: value?.countryLong,
      address: value?.address,
    };
    geocodedForRef.current = streetFields(value);
    clearedRef.current = false;
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.placeId, value?.lat, value?.lon, value?.street1]);

  // Dismiss the suggestions dropdown when the user clicks outside the street
  // input + dropdown.
  useEffect(() => {
    if (suggestions.length === 0) return;
    function handleMouseDown(e: MouseEvent) {
      if (!streetWrapperRef.current) return;
      if (!streetWrapperRef.current.contains(e.target as Node)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [suggestions.length]);

  function handleStreetChange(text: string) {
    setStreet1(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      autocompleteMutation.mutate(
        { searchText: text, sessionToken },
        {
          onSuccess: (result) => {
            setSessionToken(result.sessionToken);
            setSuggestions(result.suggestions);
          },
          onError: () => setSuggestions([]),
        },
      );
    }, 300);
  }

  function handleSelectSuggestion(s: AddressSuggestion) {
    setSuggestions([]);
    geocodeMutation.mutate(s.address, {
      onSuccess: (addr) => {
        // Compare the picked place against what's currently in the form.
        // If it's the SAME place, preserve the user's apt/unit answer
        // (street2 + noUnit) since Google rarely returns a subpremise from
        // autocomplete and we don't want to wipe valid user input.
        // If it's a DIFFERENT place, the prior unit answer no longer applies
        // — clear street2 + noUnit so the verify-row re-fires downstream.
        const currentSnapshot: Address = {
          street1: street1.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          placeId: extrasRef.current.placeId,
        };
        const samePlace = addressesMatch(currentSnapshot, addr);

        setStreet1(addr.street1 ?? '');
        setCity(addr.city ?? '');
        setStateProvince(addr.stateProvince ?? '');
        setPostalCode(addr.postalCode ?? '');
        setCountry(addr.country ?? '');
        if (!samePlace) {
          setStreet2('');
          setNoUnit(false);
          // Picking a different place replaces any prior pin location.
          setPinConfirmed(false);
        }
        extrasRef.current = {
          lat: addr.lat,
          lon: addr.lon,
          placeId: addr.placeId,
          geocoded: addr.geocoded,
          county: addr.county,
          countyLong: addr.countyLong,
          stateProvinceLong: addr.stateProvinceLong,
          countryLong: addr.countryLong,
          address: addr.address,
        };
        geocodedForRef.current = streetFields(addr);
        setSaveError(null);
      },
      onError: (err) => console.error('[AddressEditCard] geocode failed', err),
    });
  }

  const trimmedStreet1 = street1.trim();
  const trimmedStreet2 = street2.trim();
  const trimmedCity = city.trim();
  const trimmedState = stateProvince.trim();
  const trimmedPostal = postalCode.trim();
  const trimmedCountry = country.trim();
  // Apt must be resolved before saving — either a unit entered, "None"
  // explicitly tapped, or no street1 yet (apt prompt is irrelevant without
  // an address). Mirrors the verify-row condition outside the card.
  const aptResolved = !trimmedStreet1 || !!trimmedStreet2 || noUnit;
  // Full address required — every field must be present. Partials produce
  // listings/profiles the backend can't compute tax/feed/locale for.
  // A real street1 OR an explicitly-confirmed pin location both count as
  // "we know where this is" — city/state/postal/country are still required
  // for tax/locale regardless.
  const canSave =
    (!!trimmedStreet1 || pinConfirmed) &&
    !!trimmedCity &&
    !!trimmedState &&
    !!trimmedPostal &&
    !!trimmedCountry &&
    aptResolved &&
    !geocodeMutation.isPending;

  async function handleSave() {
    if (!canSave) return;
    setSaveError(null);
    const typed: Address = {
      street1: trimmedStreet1 || undefined,
      city: trimmedCity || undefined,
      stateProvince: trimmedState || undefined,
      postalCode: trimmedPostal || undefined,
      country: trimmedCountry || undefined,
    };
    // A street typed by hand (or a from-scratch entry after Clear) has no
    // coords of its own yet. Geocode it so the pin, and anything measured from
    // it like the drop-off radius, follows the text. A pin-only location skips
    // this: there the pin is the source of truth, not the text.
    const extras = extrasRef.current;
    const stale =
      !!trimmedStreet1 &&
      (extras.lat == null ||
        extras.lon == null ||
        !sameStreetFields(typed, geocodedForRef.current));
    if (stale) {
      try {
        const addr = await geocodeMutation.mutateAsync(
          `${trimmedStreet1}, ${trimmedCity}, ${trimmedState} ${trimmedPostal}, ${trimmedCountry}`,
        );
        if (!mountedRef.current) return;
        if (addr.lat == null || addr.lon == null) throw new Error('geocode returned no coords');
        // A loose match (just the ZIP, or the street without this house
        // number) would put the pin somewhere the address isn't.
        if (!geocodedStreetMatches(trimmedStreet1, addr.street1)) {
          throw new Error(`geocode matched "${addr.address}", not the street`);
        }
        extrasRef.current = {
          lat: addr.lat,
          lon: addr.lon,
          placeId: addr.placeId,
          geocoded: addr.geocoded,
          county: addr.county,
          countyLong: addr.countyLong,
          stateProvinceLong: addr.stateProvinceLong,
          countryLong: addr.countryLong,
          address: addr.address,
        };
        geocodedForRef.current = typed;
      } catch (err) {
        if (!mountedRef.current) return;
        console.error('[AddressEditCard] geocode on save failed', err);
        setSaveError(t('addressEdit.notFound'));
        return;
      }
    }
    const next: Address = {
      // After a "Clear", don't fold the original place's fields back in.
      ...(clearedRef.current ? {} : value ?? {}),
      ...extrasRef.current,
      street1: trimmedStreet1 || undefined,
      street2: trimmedStreet2 || undefined,
      city: city.trim() || undefined,
      stateProvince: stateProvince.trim() || undefined,
      postalCode: postalCode.trim() || undefined,
      country: country.trim() || undefined,
      noUnit,
      pinConfirmed,
    };
    onSave(next);
  }

  function handleStreet2Change(text: string) {
    setStreet2(text);
    // Typing a unit implicitly contradicts a prior "no unit" answer.
    if (text.trim() && noUnit) setNoUnit(false);
  }

  function handleNoUnit() {
    setStreet2('');
    setNoUnit(true);
  }

  function handleClearNoUnit() {
    setNoUnit(false);
  }

  // Wipe every field so the user can type a fresh address from scratch. Also
  // drops the geocoded extras (lat/lon/placeId/etc.) — they belonged to the
  // old place and would otherwise be stitched back on in handleSave.
  function handleClearAll() {
    setStreet1('');
    setStreet2('');
    setCity('');
    setStateProvince('');
    setPostalCode('');
    setCountry('');
    setNoUnit(false);
    setPinConfirmed(false);
    setSuggestions([]);
    setSaveError(null);
    extrasRef.current = {};
    clearedRef.current = true;
  }

  const inputStyle = {
    backgroundColor: 'var(--ion-background-color)',
    color: 'var(--ion-text-color)',
    borderColor: 'var(--ion-color-medium-tint)',
  } as const;
  const inputClass =
    'w-full rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)]';

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border p-4"
      style={{
        backgroundColor: 'var(--ion-background-color)',
        borderColor: 'var(--ion-color-medium-tint)',
      }}
    >
      {/* Header: title + close */}
      <div className="flex items-center justify-between">
        <h3
          className="text-base font-semibold"
          style={{ color: 'var(--ion-color-primary)' }}
        >
          {t('addressEdit.title')}
        </h3>
        <button
          type="button"
          aria-label={t('common:actions.close')}
          onClick={onCancel}
          className="rounded p-1"
          style={{ color: 'var(--ion-text-color-step-300)' }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Street + Apt. Street takes its own full-width line; the Apt input gets
          its own line. When the Apt input is empty, a "no unit" checkbox appears
          beneath it — checking it is what resolves the apt requirement. Once a
          unit is typed the checkbox disappears (the entry itself resolves it). */}
      <div className="flex flex-col gap-2">
        <div ref={streetWrapperRef} className="relative">
          <input
            type="text"
            value={street1}
            onChange={(e) => handleStreetChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSuggestions([]);
            }}
            placeholder={t('addressEdit.streetPlaceholder')}
            className={inputClass}
            style={inputStyle}
          />
          {suggestions.length > 0 && (
            <div
              className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border shadow-lg"
              style={{
                backgroundColor: 'var(--ion-background-color)',
                borderColor: 'var(--ion-text-color-step-800)',
              }}
            >
              {suggestions.map((s) => (
                <button
                  key={s.placeId}
                  type="button"
                  onClick={() => handleSelectSuggestion(s)}
                  className="w-full border-b px-3 py-2 text-left text-sm hover:bg-surface-50 last:border-b-0"
                  style={{
                    color: 'var(--ion-text-color)',
                    borderBottomColor: 'var(--ion-text-color-step-800)',
                  }}
                >
                  {s.address}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Apt/Unit field with an inline "no unit" checkbox. The input shrinks
            (min-w-0) so the checkbox always fits on the same line. Border turns
            warning-colored when the apt is unresolved (street1 set but no street2
            and !noUnit). The checkbox only shows while the field is empty; ticking
            it resolves the requirement in place of an entry, and typing a unit
            hides it (and clears it via handleStreet2Change). */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={street2}
            onChange={(e) => handleStreet2Change(e.target.value)}
            placeholder={t('addressEdit.aptPlaceholder')}
            className={`${inputClass} min-w-0 flex-1`}
            style={{
              ...inputStyle,
              borderColor: !aptResolved
                ? 'var(--ion-color-warning-shade)'
                : 'var(--ion-color-medium-tint)',
            }}
          />
          {!trimmedStreet2 && (
            <label
              className="flex shrink-0 cursor-pointer items-center gap-2 text-sm"
              style={{ color: 'var(--ion-text-color)' }}
            >
              <input
                type="checkbox"
                checked={noUnit}
                onChange={(e) =>
                  e.target.checked ? handleNoUnit() : handleClearNoUnit()
                }
                style={{ accentColor: 'var(--ion-color-primary)' }}
              />
              {t('addressEdit.noUnitCheckbox')}
            </label>
          )}
        </div>
      </div>

      {/* City + State + Postal. City on its own full-width line; State + Zip
          share a line, each shrinkable (min-w-0) so they stay inside the card
          at any text size. */}
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder={t('addressEdit.cityPlaceholder')}
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)]"
          style={inputStyle}
        />
        {/* State + Zip sit side-by-side when there's room, but each keeps a
            readable minimum width — once they'd get too narrow (e.g. large text)
            they wrap onto their own full-width lines instead of squishing. */}
        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-[6rem] flex-1">
            <select
              value={stateProvince}
              onChange={(e) => setStateProvince(e.target.value)}
              className="w-full appearance-none rounded-lg border px-3 py-2 pr-7 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)]"
              style={inputStyle}
            >
              <option value="">{t('addressEdit.stateEmpty')}</option>
              {stateProvince && !US_STATES.includes(stateProvince) && (
                <option value={stateProvince}>{stateProvince}</option>
              )}
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--ion-text-color-step-300)' }}
            />
          </div>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder={t('addressEdit.zipPlaceholder')}
            className="min-w-[6rem] flex-1 rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)]"
            style={inputStyle}
          />
        </div>
      </div>

      {saveError && (
        <p className="text-sm" style={{ color: 'var(--ion-color-danger)' }}>
          {saveError}
        </p>
      )}

      {/* Clear (left) + Save (right). Clear wipes every field so the user can
          enter an address from scratch. */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={handleClearAll}
          className="rounded-lg border px-4 py-2 text-sm font-medium"
          style={{
            backgroundColor: 'var(--ion-background-color)',
            borderColor: 'var(--ion-color-medium-tint)',
            color: 'var(--ion-text-color)',
          }}
        >
          {t('addressEdit.clearFields')}
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!canSave}
          className="rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-50"
          style={{
            backgroundColor: 'var(--ion-color-primary)',
            color: 'var(--ion-color-primary-contrast)',
          }}
        >
          {t('common:actions.save')}
        </button>
      </div>
    </div>
  );
}
